import type { HttpAdapter } from './HttpAdapter';
import type {
  LoginRequestDto,
  LoginResponseDto,
  PasskeyVerifyResponseDto,
  RefreshTokenRequestDto,
  TokenDto,
  ServiceRequestDto,
  ServiceResponseDto,
  ServiceType,
} from '../../types/api';
import { API_CONSTANTS } from '../../config/constants';
import { resolveAuthContext } from '../auth/authContext';
import type {
  SecureLoginApiResponseDto,
  SecureLoginResult,
  TokenAssessmentMode,
} from '../../types/api/tokenAssessment';

/**
 * API Service - Main service for backend communication
 */
export class ApiService {
  private baseUrl: string;
  private httpAdapter: HttpAdapter;

  /**
   * Guards against concurrent backend token-refresh calls. Without this, multiple
   * requests that each cross the proactive-refresh age threshold (or that each
   * receive a 401) around the same time would independently call
   * POST /api/Authentication/refreshtoken — if refresh tokens are single-use/
   * rotating, the losing concurrent callers would invalidate each other's refresh
   * token and cause spurious logouts. All callers that arrive while a refresh is
   * already in flight are coalesced onto the same promise instead.
   */
  private refreshInFlight: Promise<boolean> | null = null;

  constructor(baseUrl: string, httpAdapter: HttpAdapter) {
    this.baseUrl = baseUrl;
    this.httpAdapter = httpAdapter;
  }

  /**
   * Get backend URL for debugging
   */
  getBackendUrl(): string {
    return this.baseUrl;
  }

  /**
   * Test connectivity to backend server
   */
  async testConnectivity(): Promise<boolean> {
    console.log('🔍 Testing backend connectivity...');
    console.log(`   Backend URL: ${this.baseUrl}`);
    
    try {
      // Try to make a simple GET request to the API base
      const response = await this.httpAdapter.get('/api');
      console.log('✅ Backend is reachable');
      console.log(`   Status: ${response.status}`);
      return true;
    } catch (error) {
      console.log('❌ Backend connectivity test failed');
      console.log(`   Error:`, (error as any)?.message || error);
      
      // Try the login endpoint as a fallback test
      try {
        console.log('🔄 Trying login endpoint as connectivity test...');
        await this.httpAdapter.post(this.authEndpoints.LOGIN, {});
      } catch (loginError) {
        console.log('❌ Login endpoint also unreachable');
        console.log(`   Error:`, (loginError as any)?.message || loginError);
        if ((loginError as any)?.code === 'ECONNREFUSED') {
          console.log('💡 Suggestion: Check if backend server is running');
        }
        if ((loginError as any)?.code === 'ENOTFOUND') {
          console.log('💡 Suggestion: Check network connectivity and URL');
        }
      }
      return false;
    }
  }

  // Authentication endpoints (relative paths - base URL handled by HTTP adapter)
  private get authEndpoints() {
    return {
      LOGIN: '/api/Authentication/login',
      REFRESH_TOKEN: '/api/Authentication/refreshtoken',
      LOGOUT: '/api/Authentication/logout',
      SEND_CODE: '/api/Authentication/sendcode',
      PASSKEY_CHALLENGE: '/api/Authentication/passkey/challenge',
      PASSKEY_VERIFY: '/api/Authentication/passkey/verify',
      /**
       * Exchanges a /secure or /remote launch token (LaunchSecureDto.SSKey) for a
       * scoped JWT pair, the same way LOGIN exchanges a password — this is the
       * ONLY place that mints a token for a token-based administration session,
       * deliberately kept in AuthenticationController rather than a separate
       * "assessment resolve" endpoint (there is no such controller on the
       * backend; an earlier attempt to call one was a dead-end — see
       * types/api/tokenAssessment.ts).
       */
      SECURE_LOGIN: '/api/Authentication/secure-login',
    };
  }

  // Service endpoints (relative paths - base URL handled by HTTP adapter)
  private get serviceEndpoints() {
    return {
      ACCOUNT: '/api/services/account',
      SYNC: '/api/services/sync',
      CONTENT: '/api/services/content',
      DATA: '/api/services/data',
      NOTIFY: '/api/services/notify',
      LOG: '/api/services/log',
      MKT: '/api/services/mkt',
      REPORT: '/api/services/report',
    };
  }

  /**
   * Authenticate user with backend
   */
  async authenticate(request: LoginRequestDto): Promise<LoginResponseDto> {
    const url = this.authEndpoints.LOGIN;
    
    try {
      const response = await this.httpAdapter.post<LoginResponseDto>(
        url,
        request,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error) {
      console.log('❌ HTTP request failed');
      console.log(`   Error:`, error);
      if ((error as any)?.response) {
        console.log(`   HTTP Status: ${(error as any).response.status}`);
        console.log(`   Response Data:`, (error as any).response.data);
      }
      if ((error as any)?.code) {
        console.log(`   Error Code: ${(error as any).code}`);
      }
      throw error;
    }
  }

  /**
   * Exchange a /secure, /remote, /link, or /share launch token into a scoped administration
   * session — the token-based equivalent of `authenticate()`. Every mode hits this same
   * SecureLogin endpoint with the same SecureLoginRequestDto shape ({Token, Mode}) — the mode
   * string is the only thing that varies per entry point, not the endpoint or request shape.
   * On success, mints and applies a real JWT to this ApiService instance (`setAuthToken`) so
   * every subsequent ServicesController call this instance makes (content sync,
   * admin_response_submit, admin_state_suspend, etc.) is authenticated as this one respondent
   * session, not an SRA login. See `types/api/tokenAssessment.ts` for the full response contract.
   */
  async resolveSecureAdministration(
    mode: TokenAssessmentMode,
    token: string,
  ): Promise<SecureLoginResult> {
    try {
      const response = await this.httpAdapter.post<SecureLoginApiResponseDto>(
        this.authEndpoints.SECURE_LOGIN,
        { Token: token, Mode: mode },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const envelope = response.data;
      if (!envelope?.success || !envelope.data) {
        return { success: false, message: envelope?.message || 'Invalid token response' };
      }

      this.setAuthToken(envelope.data.token);
      return { success: true, data: envelope.data };
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message ?? error?.message ?? 'Failed to resolve secure administration session',
      };
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(request: RefreshTokenRequestDto): Promise<{ success: boolean; data?: any; message?: string }> {
    const url = this.authEndpoints.REFRESH_TOKEN;
    
    try {
      const response = await this.httpAdapter.post<any>(
        url,
        request,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      // Check if the response has the new RefreshTokenResponseDto structure
      if (response.data?.success && response.data?.data) {
        const responseData = response.data.data;
        
        return {
          success: true,
          data: responseData  // Return the full data structure including profiles and adminHistory
        };
      } 
      else {
        return {
          success: false,
          message: (response.data as any)?.message || 'Invalid response structure - no token data found'
        };
      }
    } catch (error) {
      console.log('❌ Token refresh HTTP request failed');
      console.log(`   Error:`, error);
      if ((error as any)?.response) {
        console.log(`   HTTP Status: ${(error as any).response.status}`);
        console.log(`   Response Data:`, (error as any).response.data);
      }
      if ((error as any)?.code) {
        console.log(`   Error Code: ${(error as any).code}`);
      }
      
      return {
        success: false,
        message: (error as any)?.response?.data?.message || (error as any)?.message || 'Token refresh failed'
      };
    }
  }

  /**
   * Process service request
   */
  async processServiceRequest(
    request: ServiceRequestDto,
    serviceType: ServiceType = 'sync'
  ): Promise<ServiceResponseDto> {
    const endpointMap = {
      account: this.serviceEndpoints.ACCOUNT,
      sync: this.serviceEndpoints.SYNC,
      content: this.serviceEndpoints.CONTENT,
      data: this.serviceEndpoints.DATA,
      notify: this.serviceEndpoints.NOTIFY,
      log: this.serviceEndpoints.LOG,
      mkt: this.serviceEndpoints.MKT,
      report: this.serviceEndpoints.REPORT,
    };

    const endpoint = endpointMap[serviceType];
    console.log(`🔧 processServiceRequest: calling httpAdapter.post`, { endpoint, serviceType, requestType: request.RequestType });

    let response;
    try {
      response = await this.httpAdapter.post<ServiceResponseDto>(
        endpoint,
        request,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    } catch (err: any) {
      console.error(`❌ processServiceRequest: httpAdapter.post threw`, { endpoint, message: err?.message, status: err?.response?.status, err });
      throw err;
    }

    return response.data;
  }

  /**
   * Debug-only entry point for exercising the same refresh-token response
   * handling used by the real proactive/reactive refresh paths, without
   * requiring callers to reach into the private `processRefreshResponse`
   * via an `as any` cast. Intended for DebugView's manual "Test RefreshAuth"
   * button — not part of the normal request flow.
   */
  async debugForceRefresh(
    refreshResponse: any,
    userStore: any,
    storage?: any
  ): Promise<boolean> {
    const userStoreState = userStore?.getState ? userStore.getState() : userStore;
    return this.processRefreshResponse(refreshResponse, userStoreState, userStoreState, storage);
  }

  /**
   * Process refresh token response and update tokens/session
   */
  private async processRefreshResponse(
    refreshResponse: any,
    userStore: any,
    userStoreState: any,
    storage?: any
  ): Promise<boolean> {
    if (refreshResponse?.success && refreshResponse.data?.token) {
      const newToken = refreshResponse.data.token;
      const newRefreshToken = refreshResponse.data.refreshToken;
      const profileData = refreshResponse.data.profiles;
      // Handle both camelCase (adminHistory) and all-lowercase (adminhistory) — the C# serializer
      // can return either depending on the controller's serializer settings.  Normalise here so
      // every downstream consumer only needs to handle camelCase.
      const adminHistoryRaw = refreshResponse.data.adminHistory ?? (refreshResponse.data as any).adminhistory;
      const adminHistoryData: typeof adminHistoryRaw = adminHistoryRaw
        ? { adminHistory: adminHistoryRaw.adminHistory ?? (adminHistoryRaw as any).adminhistory ?? [] }
        : undefined;
      console.log('✅ processServiceRequestWithRefresh: Backend refresh token successful');
      
      // Update stored tokens using the storage reference
      if (storage && storage.setItem) {
        await storage.setItem('auth_token', newToken);
        if (newRefreshToken) {
          await storage.setItem('refresh_token', newRefreshToken);
        }
        await storage.setItem('auth_timestamp', new Date().toISOString());
      }
      
      // Update API service with new token
      this.setAuthToken(newToken);
      
      // Update user session with new tokens
      if (userStoreState.session?.user) {
        userStoreState.session.user.authToken = newToken;
        if (newRefreshToken) {
          userStoreState.session.user.refreshToken = newRefreshToken;
        }
        
        // Update stored user session using the storage reference
        if (storage && storage.setItem) {
          const sessionData = {
            user: userStoreState.session.user,
            familyAccount: userStoreState.session.familyAccount,
            isAuthenticated: true,
            sessionStartTime: userStoreState.session.sessionStartTime || new Date().toISOString()
          };
          await storage.setItem('userSession', JSON.stringify(sessionData));
        }
      }

      // Pro Groups no longer runs legacy ChildNav profile/admin-history sync on token refresh.
      void profileData;
      void adminHistoryData;
      void userStore;
      
      return true;
    } else {
      const refreshError = refreshResponse?.message || 'Unknown refresh error';
      console.warn('🔐 processServiceRequestWithRefresh: Backend refresh token failed:', refreshError);
      
      // Check if this is an invalid/expired refresh token error
      if (refreshError.includes('Invalid or expired refresh token') || 
          refreshError.includes('invalid') || 
          refreshError.includes('expired')) {
        console.log('🔐 processServiceRequestWithRefresh: Refresh token is expired/invalid - clearing ONLY refresh token');
        
        // Only clear the refresh_token — do NOT clear auth_token here.
        // Removing auth_token would break any concurrent or subsequent request that still
        // carries a valid access token (e.g., immediately after a fresh passkey sign-in
        // where processServiceRequestWithRefresh is called before restoreSessionFromStorage restores
        // the in-memory session). The access token is cleared only when the original
        // request itself returns 401, not when the refresh side-channel fails.
        if (storage && storage.removeItem) {
          try {
            await storage.removeItem('refresh_token');
            console.log('🗑️ processServiceRequestWithRefresh: Cleared invalid refresh token from storage');
          } catch (clearError) {
            console.warn('🗑️ processServiceRequestWithRefresh: Failed to clear refresh token:', clearError);
          }
        }
      }
      
      return false;
    }
  }

  private async getAuthTimestampAgeMinutes(storage?: any): Promise<number | null> {
    if (!storage?.getItem) {
      return null;
    }

    try {
      const authTimestamp = await storage.getItem('auth_timestamp');
      if (!authTimestamp) {
        return null;
      }

      const timestampMs = Date.parse(authTimestamp);
      if (Number.isNaN(timestampMs)) {
        return null;
      }

      return (Date.now() - timestampMs) / 60000;
    } catch (error) {
      console.warn('⚠️ ApiService: failed to read auth_timestamp', error);
      return null;
    }
  }

  /**
   * Refresh the stored authentication token against the backend, coalescing
   * concurrent callers onto a single in-flight refresh (see `refreshInFlight`).
   * Both the proactive (age-based) and reactive (401-triggered) refresh paths
   * in `processServiceRequestWithRefresh` call this same guarded method.
   */
  private async refreshStoredAuthentication(
    userStore: any,
    storage?: any
  ): Promise<boolean> {
    if (!userStore) {
      return false;
    }

    if (this.refreshInFlight) {
      console.log('🔐 ApiService: refresh already in flight, awaiting existing promise');
      return this.refreshInFlight;
    }

    this.refreshInFlight = this.doRefreshStoredAuthentication(userStore, storage);
    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  private async doRefreshStoredAuthentication(
    userStore: any,
    storage?: any
  ): Promise<boolean> {
    try {
      const userStoreState = userStore.getState ? userStore.getState() : userStore;
      const sessionRefreshToken = userStoreState.session?.user?.refreshToken;
      const storageRefreshToken = storage?.getItem ? await storage.getItem('refresh_token') : null;
      const refreshToken = sessionRefreshToken || storageRefreshToken;

      console.log('🔑 ApiService: refresh token availability check', {
        hasSessionRefreshToken: !!sessionRefreshToken,
        hasStorageRefreshToken: !!storageRefreshToken,
        hasRefreshToken: !!refreshToken,
      });

      if (!refreshToken) {
        return false;
      }

      const currentUser = userStoreState.session?.user;
      const refreshResponse = await this.refreshToken({
        refreshToken,
        SraId: currentUser?.userId || (currentUser as any)?.id || '',
        AccessType: API_CONSTANTS.DEFAULT_ACCESS_TYPE,
      });

      const refreshSuccess = await this.processRefreshResponse(
        refreshResponse,
        userStore,
        userStoreState,
        storage
      );

      if (refreshSuccess) {
        return true;
      }

      const refreshError = refreshResponse?.message || 'Unknown refresh error';
      console.warn('🔐 ApiService: backend refresh failed:', refreshError);

      if (refreshError.includes('Invalid or expired refresh token')
        || refreshError.includes('invalid')
        || refreshError.includes('expired')) {
        if (storage?.removeItem) {
          try {
            await storage.removeItem('refresh_token');
          } catch (clearError) {
            console.warn('🗑️ ApiService: failed to clear expired refresh token', clearError);
          }
        }
      }

      return false;
    } catch (error) {
      console.warn('🔐 ApiService: backend refresh attempt failed (network/unexpected error)', error);
      // NOTE: userStoreState.restoreSessionFromStorage() re-hydrates already-persisted
      // tokens from storage — it makes NO network call and does NOT confirm the token
      // is still valid server-side. We still call it so a transient error doesn't wipe
      // in-memory session state, but a `true` return here is a LOCAL restore, not a
      // proven backend refresh — the next request may still 401.
      //
      // Deliberately reads from `userStoreState` (the resolved `.getState()` result),
      // not `userStore` (the raw hook/getter) — the hook itself doesn't carry actions,
      // only the state it returns does. Calling this on `userStore` directly is a
      // no-op that silently skips the fallback.
      const userStoreState = userStore?.getState ? userStore.getState() : userStore;
      if (userStoreState?.restoreSessionFromStorage) {
        try {
          const restoredLocally = await userStoreState.restoreSessionFromStorage();
          if (restoredLocally) {
            console.warn('🔐 ApiService: local session re-hydrated after refresh error — this does not confirm the token is valid server-side');
          }
          return restoredLocally;
        } catch (fallbackError) {
          console.warn('🔐 ApiService: local restoreSessionFromStorage fallback failed', fallbackError);
        }
      }

      return false;
    }
  }

  /**
   * Process service request with automatic 401 retry handling
   * This method wraps processServiceRequest with automatic JWT token refresh on 401 errors
   */
  async processServiceRequestWithRefresh(
    request: ServiceRequestDto,
    serviceType: ServiceType = 'sync',
    options?: {
      userStore?: any; // UserStore instance for authentication and token management 
      storage?: any; // Storage interface for token persistence
      checkNetworkConnectivity?: () => Promise<boolean>; // Function to check network connectivity
    }
  ): Promise<ServiceResponseDto> {
    const authContext = resolveAuthContext(options);

    const authAgeMinutes = await this.getAuthTimestampAgeMinutes(authContext.storage);
    if (authAgeMinutes !== null && authAgeMinutes >= 55 && authContext.userStore) {
      console.log('🔄 ApiService: proactive auth refresh triggered before request', {
        requestType: request.RequestType,
        authAgeMinutes,
      });
      await this.refreshStoredAuthentication(authContext.userStore, authContext.storage);
    }

    try {
      const result = await this.processServiceRequest(request, serviceType);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ processServiceRequestWithRefresh: processServiceRequest threw', {
        message: errorMessage,
        status: (error as any)?.response?.status,
        error,
      });
      
      // Check if this is a 401 authentication error
      const is401Error = (error as any)?.response?.status === 401 ||
                        (error as any)?.status === 401 ||
                        errorMessage.includes('401') ||
                        errorMessage.includes('Authentication required') ||
                        errorMessage.includes('Invalid or missing JWT token');

      if (!is401Error) {
        // Not a 401 error, re-throw original error
        console.log('❌ processServiceRequestWithRefresh: Non-401 error, re-throwing:', errorMessage);
        throw error;
      }

      console.log('🔐 processServiceRequestWithRefresh: 401 detected, attempting automatic re-authentication...');

      // Validate required options for 401 handling
      if (!authContext.userStore) {
        console.error('❌ processServiceRequestWithRefresh: userStore required for 401 handling but not provided');
        throw new Error('Authentication error: userStore required for token refresh');
      }

      try {
        // Check network connectivity first if function provided
        if (options?.checkNetworkConnectivity) {
          const hasNetwork = await options.checkNetworkConnectivity();
          if (!hasNetwork) {
            console.log('❌ processServiceRequestWithRefresh: No network connectivity - will not retry');
            throw new Error('Session expired - no network connection');
          }
        }

        const refreshSuccess = await this.refreshStoredAuthentication(authContext.userStore, authContext.storage);
        
        // Step 2: If we successfully restored authentication, retry the request
        if (refreshSuccess) {
          console.log('✅ processServiceRequestWithRefresh: Authentication restored, retrying request...');
          
          try {
            console.log('🔄 processServiceRequestWithRefresh: Making retry request...');
            const retryResponse = await this.processServiceRequest(request, serviceType);
            
            console.log('📬 processServiceRequestWithRefresh: Retry response received:', {
              success: retryResponse?.success,
              hasPayload: !!retryResponse?.payload,
              timestamp: new Date().toISOString()
            });
            
            return retryResponse;
          } catch (retryError) {
            console.error('❌ processServiceRequestWithRefresh: Retry request failed:');
            console.error('🔍 Error type:', typeof retryError);
            console.error('🔍 Error message:', (retryError as any)?.message);
            
            const retryErrorMessage = retryError instanceof Error ? retryError.message : String(retryError);
            console.error(`❌ processServiceRequestWithRefresh: Final retry error: ${retryErrorMessage}`);
            
            throw new Error(`Auth retry error: ${retryErrorMessage}`);
          }
        } else {
          // Authentication restoration failed - determine the reason
          console.log('❌ processServiceRequestWithRefresh: Could not restore authentication');
          
          throw new Error('Auth recovery failed - manual login required');
        }
        
      } catch (connectivityError) {
        console.error('❌ processServiceRequestWithRefresh: Re-authentication process failed:', connectivityError);
        throw new Error(`Session expired - re-auth process failed: ${connectivityError instanceof Error ? connectivityError.message : String(connectivityError)}`);
      }
    }
  }

  /**
   * Sync service request
   */
  async syncService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'sync');
  }

  /**
   * Content service request
   */
  async contentService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'content');
  }

  /**
   * Account service request
   */
  async accountService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'account');
  }

  /**
   * Data service request
   */
  async dataService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'data');
  }

  /**
   * Notification service request
   */
  async notifyService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'notify');
  }

  /**
   * Logging service request
   */
  async logService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'log');
  }

  /**
   * Marketing service request
   */
  async mktService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'mkt');
  }

  /**
   * Reporting service request
   */
  async reportService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'report');
  }

  /**
   * Request a verification code to be sent to the given email address.
   * The backend is expected to deliver a short-lived OTP via email/SMS.
   */
  async sendVerificationCode(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.httpAdapter.post<{ success: boolean; message: string }>(
        this.authEndpoints.SEND_CODE,
        { email },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data ?? { success: true, message: 'Code sent' };
    } catch (error: any) {
      const status = error?.response?.status ?? 0;
      const message = error?.response?.data?.message ?? error?.message ?? 'Failed to send code';
      // Treat 404 as endpoint-not-implemented (backend not ready yet)
      if (status === 404) {
        return { success: false, message: 'Verification code endpoint not available on this server' };
      }
      return { success: false, message };
    }
  }

  /**
   * Request a WebAuthn / passkey challenge from the backend.
   *
   * POST /api/Authentication/passkey/challenge
   * Body: { email, accessType, userId? }
   *
   * The backend returns:
   *   { success, data: { challengeId, challenge, relyingPartyId, relyingPartyName,
   *                      userVerification, timeout,
   *                      allowCredentials? (auth) | user? (registration) } }
   *
   * Callers inspect `data.allowCredentials` to decide authenticate vs. register.
   */
  async getPasskeyChallenge(
    email: string,
    accessType: string,
    userId?: string | null,
    userName?: string | null,
    skinType?: string | null,
  ): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const body: Record<string, unknown> = { email, accessType };
      if (userId) body.userId = userId;
      if (userName) body.userName = userName;
      if (skinType) body.skinType = skinType;
      const response = await this.httpAdapter.post<any>(
        this.authEndpoints.PASSKEY_CHALLENGE,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );
      const envelope = response.data;
      // Backend wraps response in { success, data, message }
      if (envelope?.success === false) {
        return { success: false, message: envelope.message ?? 'Challenge request failed' };
      }
      return { success: true, data: envelope?.data ?? envelope };
    } catch (error: any) {
      const message = error?.response?.data?.message ?? error?.message ?? 'Failed to get passkey challenge';
      return { success: false, message };
    }
  }

  /**
   * Submit a WebAuthn credential response to the backend for verification / registration.
   *
   * POST /api/Authentication/passkey/verify
   * Body: { challengeId, email, accessType, assertion }
   *
   * Returns a session on success: { token, refreshToken, user, ... }
   */
  async verifyPasskeyAssertion(
    challengeId: string,
    email: string,
    accessType: string,
    assertion: Record<string, unknown>,
  ): Promise<{ success: boolean; data?: PasskeyVerifyResponseDto; message?: string }> {
    try {
      // Body keys must match C# Pascal-case DTO properties exactly:
      // PasskeyVerifyRequestDto: ChallengeId, Email, AccessType, Assertion
      const body = {
        ChallengeId: challengeId,
        Email:       email,
        AccessType:  accessType,
        Assertion:   assertion,
      };
      const response = await this.httpAdapter.post<any>(
        this.authEndpoints.PASSKEY_VERIFY,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );
      const envelope = response.data;
      if (envelope?.success === false) {
        return { success: false, message: envelope.message ?? 'Passkey verification failed' };
      }
      return { success: true, data: envelope?.data ?? envelope };
    } catch (error: any) {
      const message = error?.response?.data?.message ?? error?.message ?? 'Passkey verification failed';
      return { success: false, message };
    }
  }

  /**
   * Set authentication token for subsequent requests
   */
  setAuthToken(token: string | null): void {
    console.log('🔐 ApiService: Setting auth token:', token ? '[SET]' : '[CLEARED]');
    this.httpAdapter.setAuthToken(token);
  }

  /**
   * Get current authentication token
   */
  getAuthToken(): string | null {
    return this.httpAdapter.getAuthToken();
  }

  /**
   * Initialize API service with stored credentials
   */
  async initializeWithStorage(storage: any): Promise<void> {
    console.log('🔄 ApiService: Initializing with stored credentials...');
    
    try {
      const storedToken = await storage.getItem('auth_token');
      if (storedToken) {
        console.log('✅ ApiService: Found stored auth token, setting for API requests');
        this.setAuthToken(storedToken);
        
        // Optionally verify token is still valid
        const userEmail = await storage.getItem('user_email');
        console.log('📧 ApiService: Associated email:', userEmail || 'Not found');
      } else {
        console.log('ℹ️ ApiService: No stored auth token found, API will use anonymous requests');
      }
    } catch (error) {
      console.error('❌ ApiService: Error loading stored credentials:', error);
    }
  }
  /**
   * Delete a child profile from the backend.
   * TODO: Implement once the backend DELETE /profiles/{profileId} endpoint is available.
   * @param profileId - The ID of the profile to delete
   * @param accountId - The parent account ID that owns the profile
   */
  async deleteProfile(profileId: string, accountId: string): Promise<void> {
    // Placeholder — backend endpoint not yet implemented.
    // When ready, replace with:
    //   await this.httpAdapter.delete(`${this.baseUrl}/profiles/${profileId}`, {
    //     headers: { Authorization: `Bearer ${this.authToken}` },
    //   });
    console.warn(
      `⚠️ ApiService.deleteProfile: Backend endpoint not yet implemented. ` +
      `Profile ${profileId} (account ${accountId}) removed locally only.`
    );
  }}